import { Box, Paper, Slider, Typography, Button, Chip, useTheme } from "@mui/material";
import { FiPlus, FiRefreshCw, FiBarChart2, FiChevronRight, FiLayers, FiActivity, FiBriefcase, FiShield, FiClock, FiInfo } from "react-icons/fi";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../components/button/RefreshButton";
import PivotableBarChart from "../../components/chart/PivotableBarChart";
import { MainContainer } from "../../components/container/MainContainer";
import ResizableBox from "../../components/container/ResizableBox";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef, translationFormater } from "../../components/grid/columnDefinitions/columnDefinitions";
import CheckBox from "../../components/input/CheckBox";
import SelectBox from "../../components/input/SelectBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import { ReportStatus } from "./ReportTypes";
import "../../style/pages-style/Organization/organizationDashboard.css";
import "../../style/pages-style/Engagement/engagement.css";
import MetricCard from "../../app_components/MetricCard";
import SearchBox from "../../components/input/SearchBox";
import { TableFooterContainer } from "../../components/container/TableFooterContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { AgGridReact } from "ag-grid-react";
import EngagementDashboardGlobalView from "../Engagement/EngagementDashboardGlobalView";
import { useNavigate } from "react-router-dom";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const Client_Directory_MySubmissions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const table_1_ref = useRef();
  const header_1_ref = useRef();
  const table_2_ref = useRef();
  const header_2_ref = useRef();
  const { columnVisibilities, refresh, setRefresh } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const emission = { label: "Emission", value: "Emission", company_field: "gwp", report_field: "gwp_per_unit" };
  const cost = { label: "Cost", value: "Cost", company_field: "cost", report_field: "cost_per_unit" };
  const comparisonOptions = [emission, cost];
  const [comparison, setComparison] = useState(emission.value);
  const [reports, setReports] = useState([]);
  const [acceptedReports, setAcceptedReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [optionCompanies, setOptionCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [optionYears, setOptionYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [emissionWeight, setEmissionWeight] = useState(50);
  const [status, setStatus] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showColVisibilityMenu_1, setShowColVisibilityMenu_1] = useState(false);
  const [showColVisibilityMenu_2, setShowColVisibilityMenu_2] = useState(false);
  const [searchText_1, setSearchText_1] = useState("");
  const [searchText_2, setSearchText_2] = useState("");
  const [showGraph_2, setShowGraph_2] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);
  const [globalView, setGlobalView] = useState(false);
  const [itemGlobalView, setItemGlobalView] = useState(false);
  const [pendingResponses, setPendingResponses] = useState([]);
  const colVisibility_key = "client_directory_my_submissions_company_table";
  const colVisibility_key_2 = "client_directory_my_submissions_report_table";
  const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
  const isHidden_2 = (field) => !columnVisibilities[colVisibility_key_2]?.includes(field);
  const costWeight = 100 - emissionWeight;

  const colDefs = [
    { field: "name", headerName: t("Client Name"), ...leftAlignColDef, ...defaultColDef },
    { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    {
      field: "cost_per_gwp",
      headerName: t("Cost/GWP"),
      ...rightAlignColDef,
      ...defaultColDef,
      valueGetter: (params) => {
        if (!params.data || !params.data.gwp || params.data.gwp === 0) return null;
        return Number(params.data.cost || 0) / Number(params.data.gwp);
      },
      valueFormatter: (params) => {
        if (params.value == null) return "—";
        return params.value.toFixed(4);
      },
    },
    { field: "report_count", headerName: t("Submissions"), ...centerAlignColDef, ...defaultColDef },
    {
      field: "avg_gwp_per_unit",
      headerName: t("Avg GWP/Unit"),
      ...rightAlignColDef,
      ...defaultColDef,
      valueFormatter: (params) => {
        if (params.value == null) return "—";
        return params.value.toFixed(2);
      },
    },
    {
      field: "total_quantity",
      headerName: t("Quantity"),
      ...rightAlignColDef,
      ...defaultColDef,
      valueFormatter: (params) => {
        if (!params.value) return "—";
        return params.value.toLocaleString();
      },
    },
    {
      field: "last_submitted",
      headerName: t("Last Submitted"),
      ...rightAlignColDef,
      ...defaultColDef,
      valueFormatter: (params) => {
        if (!params.value) return "—";
        try {
          return new Date(params.value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        } catch {
          return params.value;
        }
      },
    },
  ];
  const colDefs_2 = [
    { field: "name", headerName: t("Name"), ...leftAlignColDef, ...defaultColDef },
    { field: "year", headerName: t("Year"), ...centerAlignColDef, ...defaultColDef },
    { field: "quantity", headerName: t("Quantity"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "cost", headerName: t("Cost"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "cost_per_unit", headerName: t("Cost Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "gwp", headerName: t("GWP"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "gwp_per_unit", headerName: t("GWP Per Unit"), ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "functional_unit", headerName: t("Functional Unit"), ...centerAlignColDef, ...defaultColDef },
    { field: "other_unit", headerName: t("Other Unit"), ...centerAlignColDef, ...defaultColDef },
    { field: "level", headerName: t("Level"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
    { field: "scope", headerName: t("Scope"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
    { field: "status", headerName: t("Status"), ...centerAlignColDef, ...defaultColDef, ...translationFormater(t) },
    { field: "last_modified", headerName: t("Last Modified"), ...rightAlignColDef, ...defaultColDef },
    { field: "created_at", headerName: t("Created At"), ...rightAlignColDef, ...defaultColDef },
  ];

  const columnDefs = useMemo(() => colDefs.map((col) => ({ ...col, hide: isHidden(col.field) })), [colDefs, columnVisibilities]);
  const columnDefs_2 = useMemo(() => colDefs_2.map((col) => ({ ...col, hide: isHidden_2(col.field) })), [colDefs_2, columnVisibilities]);

  const graphs_2 = [
    { title: "GWP", countBy: "gwp" },
    { title: "Cost", countBy: "cost" },
    { title: "GWP Per Unit", countBy: "gwp_per_unit" },
    { title: "Cost Per Unit", countBy: "cost_per_unit" },
  ];

  useEffect(() => {
    let temp = [...reports];
    if (selectedCompany) {
      temp = temp.filter((r) => r?.receiver_id === selectedCompany);
    }
    if (selectedYear) {
      temp = temp.filter((r) => r?.year === selectedYear);
    }
    if (status) {
      temp = temp.filter((r) => r?.status === status);
    }
    setAcceptedReports(temp);
  }, [reports, selectedCompany, selectedYear, status]);

  useEffect(() => {
    Supplier_Reports.get_mine_sent()
      .then((res) => res.json())
      .then((data) => setReports(data));
  }, [refresh]);

  useEffect(() => {
    const uniqueCompanies = reports.reduce((acc, report) => {
      if (!acc.some((c) => c.value === report.receiver_id)) {
        acc.push({ label: report.receiver_name, value: report.receiver_id });
      }
      return acc;
    }, []);
    setOptionCompanies(uniqueCompanies);
  }, [reports]);

  useEffect(() => {
    setOptionYears(
      reports.reduce((acc, report) => {
        if (!acc.some((c) => c.value === report.year)) {
          acc.push({ label: String(report.year), value: report.year });
        }
        return acc;
      }, [])
    );
  }, [reports]);

  useEffect(() => {
    const temp = new Map();
    acceptedReports.forEach((report) => {
      if (temp.has(report.receiver_id)) {
        const existing = temp.get(report.receiver_id);
        existing.gwp += Number(report.gwp) || 0;
        existing.cost += Number(report.cost) || 0;
        existing.total_quantity += Number(report.quantity) || 0;
        existing.report_count += 1;
        existing.reports.push(report);
        const date = report.created_at || report.last_modified;
        if (date && (!existing.last_submitted || date > existing.last_submitted)) {
          existing.last_submitted = date;
        }
      } else {
        temp.set(report.receiver_id, {
          name: report.receiver_name,
          id: report.receiver_id,
          gwp: Number(report.gwp) || 0,
          cost: Number(report.cost) || 0,
          total_quantity: Number(report.quantity) || 0,
          report_count: 1,
          reports: [report],
          last_submitted: report.created_at || report.last_modified,
        });
      }
    });

    let arr = Array.from(temp.values());
    arr.forEach(c => {
      const gwpPerUnitTotal = c.reports.reduce((sum, r) => sum + (Number(r.gwp_per_unit) || 0), 0);
      c.avg_gwp_per_unit = gwpPerUnitTotal / c.reports.length;
    });
    setCompanies(arr);
  }, [acceptedReports]);

  useEffect(() => {
    setSelectedReports(companies.find((c) => c.id === selectedCompanies[0])?.reports || []);
  }, [companies, selectedCompanies]);

  useEffect(() => {
    if (table_1_ref.current?.api) {
      table_1_ref.current.api.setGridOption("quickFilterText", searchText_1);
    }
  }, [searchText_1]);

  useEffect(() => {
    if (table_2_ref.current?.api) {
      table_2_ref.current.api.setGridOption("quickFilterText", searchText_2);
    }
  }, [searchText_2]);

  const totalSubmissions = useMemo(() => reports.length, [reports]);
  const avgDataFidelity = 100; // Placeholder for logic
  const topMetricValue = useMemo(() => {
    if (comparison === emission.value) {
      const totalKg = reports.reduce((sum, r) => sum + (r.gwp || 0), 0);
      return `${(totalKg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`;
    }
    if (comparison === cost.value) return `$${reports.reduce((sum, r) => sum + (r.cost || 0), 0).toLocaleString()}`;
    return "N/A";
  }, [reports, comparison, emission, cost]);

  const initials = (name) => name?.split(" ").map((n) => n[0]).join("").toUpperCase() || "?";
  const avatarColor = (name) => {
    let hash = 0;
    for (let i = 0; i < (name?.length || 0); i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const colors = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#0891b2", "#16a34a", "#475569"];
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <MainContainer>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* KPI Grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: ORG_PALETTE.spacing.metricCardGap }}>
          <MetricCard title={t("My Submissions")} subtitle={t("Total records shared")} value={reports.length} icon={FiLayers} accent="#2563eb" bg="rgba(37, 99, 235, 0.08)" delay={0.1} />
          <MetricCard title={t("Global Reach")} subtitle={t("Unique clients engaged")} value={optionCompanies.length} icon={FiBriefcase} accent="#7c3aed" bg="rgba(124, 58, 237, 0.08)" delay={0.2} />
          <MetricCard title={t("Sent Impact")} subtitle={t("Total carbon shared")} value={reports.reduce((sum, r) => sum + (Number(r.gwp) || 0), 0)} unit="kg CO₂e" icon={FiActivity} accent="#0891b2" bg="rgba(8, 145, 178, 0.08)" delay={0.3} />
          <MetricCard
            title={t("Data Fidelity")}
            subtitle={t("Quality Assurance Score")}
            value={avgDataFidelity}
            unit="%"
            icon={FiShield}
            accent="#16a34a"
            bg="rgba(22, 163, 74, 0.08)"
            delay={0.4}
          />
        </Box>

        {/* ── Grid Container ── */}
        <Paper variant="Table Container" sx={{ mt: 1, borderRadius: ORG_PALETTE.radius.card, overflow: "hidden" }}>
          <TableHeaderContainer>
            <TableTitleContainer>
              <Typography variant="Table Title">{t("Submissions Portfolio")}</Typography>
            </TableTitleContainer>
            <TableHeaderToolsContainer ref={header_1_ref}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <SelectBox placeholder={t("Client")} size="small" width={230} options={optionCompanies} value={selectedCompany} onChange={(e, v) => setSelectedCompany(v?.value || null)} />
                <SelectBox placeholder={t("Year")} size="small" width={120} options={optionYears} value={selectedYear} onChange={(e, v) => setSelectedYear(v?.value || null)} />
                <SelectBox placeholder={t("Status")} size="small" width={160} value={status} options={ReportStatus.map((r) => ({ label: t(r), value: r }))} onChange={(e, v) => setStatus(v?.value || null)} />
                <Box sx={{ width: 280, ml: 1 }}>
                  <SearchBox text={searchText_1} setText={setSearchText_1} placeholder={t("Search clients...")} width={280} />
                </Box>
              </Box>
              <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu_1((p) => !p)} />
              <RefreshButton setRefresh={setRefresh} />
            </TableHeaderToolsContainer>
          </TableHeaderContainer>
          <Box>
            <AgGrid
              ref={table_1_ref}
              rowData={companies}
              columnDefs={columnDefs}
              onSelectionChanged={(e) => setSelectedCompanies(e.api.getSelectedRows().map(r => r.id))}
              domLayout="autoHeight"
              footerLeftContent={
                <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                  <CheckBox label={t("Global View")} checked={globalView} onChange={(e) => setGlobalView(e.target.checked)} />
                </Box>
              }
              footerRightContent={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{t("Total GWP")}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>{(companies || []).reduce((s, c) => s + (c.gwp || 0), 0).toLocaleString()} kg CO₂e</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{t("Total Cost")}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{(companies || []).reduce((s, c) => s + (c.cost || 0), 0).toLocaleString()}</Typography>
                  </Box>
                </Box>
              }
            />
          </Box>
        </Paper>

        {globalView && (
          <Box sx={{ mt: 1, animation: "eng-fadeInUp 0.4s ease" }}>
            <EngagementDashboardGlobalView companies={companies} entityLabel="Clients" accentColor="#2563eb" />
          </Box>
        )}

        {!globalView && selectedCompanies.length > 0 && selectedReports.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1, animation: "org-fadeInUp 0.4s ease" }}>
            <Paper variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.card, overflow: "hidden" }}>
              <TableHeaderContainer>
                <TableTitleContainer><Typography variant="Table Title">{companies.find(c => c.id === selectedCompanies[0])?.name} - {t("Granular Submission Breakdown")}</Typography></TableTitleContainer>
                <TableHeaderToolsContainer ref={header_2_ref}>
                  <Box sx={{ width: 450 }}><SearchBox text={searchText_2} setText={setSearchText_2} placeholder={t("Search line items...")} width={450} /></Box>
                  <ColumnVisibilityButton onClick={() => setShowColVisibilityMenu_2((p) => !p)} />
                </TableHeaderToolsContainer>
              </TableHeaderContainer>
              <Box>
                <AgGrid
                  ref={table_2_ref}
                  rowData={selectedReports}
                  columnDefs={columnDefs_2}
                  domLayout="autoHeight"
                  footerLeftContent={<Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}><CheckBox label={t("Item Analytics")} checked={itemGlobalView} onChange={(e) => setItemGlobalView(e.target.checked)} /></Box>}
                  footerRightContent={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{t("Total GWP")}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>{(selectedReports || []).reduce((s, r) => s + (r.gwp || 0), 0).toLocaleString()} kg CO₂e</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>{t("Total Cost")}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{(selectedReports || []).reduce((s, r) => s + (r.cost || 0), 0).toLocaleString()}</Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </Paper>

            {itemGlobalView && (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 3, alignItems: "start", animation: "org-fadeInUp 0.4s ease" }}>
                {graphs_2.map((g, i) => (
                  <Paper key={i} variant="Table Container" sx={{ borderRadius: ORG_PALETTE.radius.card, borderColor: tok.border.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card, overflow: "hidden" }}>
                    <TableHeaderContainer>
                      <TableTitleContainer><Typography variant="Table Title">{t("Itemized")} {t(g.title)} {t("Breakdown")}</Typography></TableTitleContainer>
                    </TableHeaderContainer>
                    <Box sx={{ p: 2, height: 400 }}>
                      <PivotableBarChart usages={selectedReports} groupBy={"name"} countBy={g.countBy} xAxisLabel={t(g.title)} yAxisLabel={t("Item Name")} />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ColumnMenu anchorRef={header_1_ref} visible={showColVisibilityMenu_1} onClose={() => setShowColVisibilityMenu_1(false)} colDefs={columnDefs} colVisibility_key={colVisibility_key} defaultCols={["name", "cost", "gwp", "cost_per_gwp", "report_count", "avg_gwp_per_unit", "total_quantity", "last_submitted"]} />
      <ColumnMenu anchorRef={header_2_ref} visible={showColVisibilityMenu_2} onClose={() => setShowColVisibilityMenu_2(false)} colDefs={columnDefs_2} colVisibility_key={colVisibility_key_2} defaultCols={["name"]} />
    </MainContainer>
  );
};

export default Client_Directory_MySubmissions;
